/**
 * Server state for the Release tab (docs/ux/release.md): the view, polled
 * every 1.5 s while a build job is queued or running (the LOI tab's
 * interval), and the build submission. A 404 is the no-document state, not
 * an error. When a build finishes the hub's detail is invalidated: the
 * cycle gate and the released version may have moved.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { ApiError, api, reasonOf } from '@/app/api';
import type { ReleaseView } from '@/app/types';
import { detailKey, refreshAudit } from '../useMatter';

export const releaseKey = (ref: string) => ['matter', ref, 'release'] as const;

const building = (view: ReleaseView | null | undefined): boolean => Boolean(view?.build && (view.build.state === 'queued' || view.build.state === 'running'));

export function useRelease(ref: string) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: releaseKey(ref),
    queryFn: async (): Promise<ReleaseView | null> => {
      try {
        return await api<ReleaseView>(`/api/matters/${encodeURIComponent(ref)}/release`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 5_000,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    refetchInterval: (q) => (building(q.state.data) ? 1_500 : false),
  });
  // The build's terminal state moves the cycle gate and the version list; the hub re-reads once.
  const last = useRef<string | null>(null);
  useEffect(() => {
    const state = query.data?.build?.state ?? null;
    if (last.current && (last.current === 'queued' || last.current === 'running') && (state === 'succeeded' || state === 'failed')) {
      void client.invalidateQueries({ queryKey: detailKey(ref) });
      void refreshAudit(client, ref);
    }
    last.current = state;
  }, [query.data?.build?.state, client, ref]);
  return query;
}

export function useBuildRelease(ref: string, onRefused: (reason: string) => void) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ jobId: string; state: 'queued' | 'running' }>(`/api/matters/${encodeURIComponent(ref)}/release/build`, { method: 'POST' }),
    onSuccess: (job) => {
      client.setQueryData<ReleaseView | null>(releaseKey(ref), (cur) => (cur ? { ...cur, build: { jobId: job.jobId, state: job.state }, canBuild: false } : cur));
      void client.invalidateQueries({ queryKey: releaseKey(ref) });
    },
    onError: (err, _vars, _ctx) => {
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) {
        onRefused(reasonOf(err) ?? 'The build was refused.');
        void client.invalidateQueries({ queryKey: releaseKey(ref) });
        return;
      }
      toast.error('The build was not started. Nothing changed.', { action: { label: 'Retry', onClick: () => void client.invalidateQueries({ queryKey: releaseKey(ref) }) } });
    },
  });
}

export { building };
